import _template from 'underscore.template'
import escapeHtml from 'escape-html'

/**
 * Adds a small time delay so that a function is not executed too many times sequentially.
 *
 * @param {Function} callback - The function to delay.
 * @param {number} limit - The time of delay.
 * @returns {Function} The delayed function.
 */
export function throttle( callback, limit ) {
  var wait = false
  return function () {
    if ( !wait ) {
      callback.call()
      wait = true
      setTimeout( function delayFunction() {
        wait = false
      }, limit )
    }
  }
}

/**
 * Checks if the page is rendered in a small device.
 *
 * @returns {boolean}
 */
export function isMobile() {
  return window.innerWidth <= 640
}

/**
 * Checks if the page is rendered in a medium device.
 *
 * @returns {boolean}
 */
export function isTablet() {
  return window.innerWidth <= 768
}

/**
 * Checks if the page is rendered in the styleguide.
 *
 * @returns {boolean}
 */
export function isStyleguide() {
  return document.querySelector( '.js-lean-styleguide' ) !== null
}

/**
 * Rounds number to set number of places.
 *
 * @param {number} num - The number to round.
 * @param {number} places - The number of places to rounnd to.
 * @returns {int}
 */
export function round( num, places ) {
  const multiplier = Math.pow( 10, places )
  return Math.round( num * multiplier ) / multiplier
}

/**
 * Retrieves checked input for given array of inputs.
 */
export function getOption( inputs ) {
  for ( let i = 0, length = inputs.length; i < length; i++ ) {
    if ( inputs[ i ].checked ) {
      return inputs[ i ].id
    }
  }
}

/**
 * Restricts input for the given textbox to the given inputFilter function.
 *
 * Doc for reference: https://stackoverflow.com/questions/469357/html-text-input-allow-only-numeric-input
 */
export function setInputFilter( textbox, inputFilter ) {
  [ 'input', 'keydown', 'keyup', 'mousedown', 'mouseup', 'select', 'contextmenu', 'drop' ].forEach( function ( event ) {
    textbox.addEventListener( event, function () {
      if ( inputFilter( this.value ) ) {
        this.oldValue = this.value
        this.oldSelectionStart = this.selectionStart
        this.oldSelectionEnd = this.selectionEnd
      } else if ( this.hasOwnProperty( 'oldValue' ) ) {
        this.value = this.oldValue
        this.setSelectionRange( this.oldSelectionStart, this.oldSelectionEnd )
      } else {
        this.value = ''
      }
    } )
  } )
}

/**
 * Copies text to user's clipboard.
 *
 * @param {string} text - The text to copy.
 */
export function copyToClipboard( text ) {
  const dummyInput = document.createElement( 'input' )

  /**
   * Workaround for copying text to clipboard.
   *
   * Doc: https://stackoverflow.com/questions/49618618/copy-current-url-to-clipboard
   *
   **/
  document.body.appendChild( dummyInput )
  dummyInput.value = text
  dummyInput.select()
  document.execCommand( 'copy' )
  document.body.removeChild( dummyInput )
}

/**
 * Fetch a JavaScript template for an id, and return a templating function for it.
 *
 * https://core.trac.wordpress.org/browser/tags/5.3/src/js/_enqueues/wp/util.js#L0
 *
 * @param {string} id A string that corresponds to a DOM element with an id prefixed with "tmpl-". For example, "attachment" maps to "tmpl-attachment".
 * @return {function}
 */
export function wptemplate( id ) {
  const options = {
    evaluate: /<#([\s\S]+?)#>/g,
    interpolate: /\{\{\{([\s\S]+?)\}\}\}/g,
    escape: /\{\{([^\}]+?)\}\}(?!\})/g,
    variable: 'data'
  }

  return data => _template( document.querySelector( `#tmpl-${ id }` ).innerHTML, data, options )
}

/**
 * Get a query param from a string (or URL).
 *
 * @param {string} location The string to search on.
 * @param {string} name The query param name to search.
 * @returns {string}
 */
export function getUrlParameter( location, name ) {
  name = name.replace( /[\[]/, '\\[' ).replace( /[\]]/, '\\]' )
  const regex = new RegExp( '[\\?&]' + name + '=([^&#]*)' )
  const results = regex.exec( location )
  return results === null ? '' : decodeURIComponent( results[1].replace( /\+/g, ' ' ) )
}

/**
 * Toggles the sections that were set to appear or disappear depending on the search results.
 *
 * @param {Element} container The container of the sections.
 * @param {string} togglerClass The class added to the elements to toggle display.
 * @param {bool} hide Hide the sections.
 */
export function toggleDisplaySections( container, togglerClass, hide ) {
  const elements = container.querySelectorAll( togglerClass )

  if ( hide ) {
    Array.from( elements ).forEach( element => element.classList.add( 'hidden!' ) )
  } else {
    Array.from( elements ).forEach( element => element.classList.remove( 'hidden!' ) )
  }
}

/**
 * Add highlighted data to the Algolia Search results.
 *
 * @param {object} item The item data.
 * @returns {object} The transformed data.
 */
export function replaceHighlightsRecursive( item ) {
  if ( item instanceof Object && item.hasOwnProperty( 'value' ) ) {
    item.value = escapeHtml( item.value )
    item.value = item.value
      .replace( /__ais-highlight__/g, '<span class="bg-yellow">' )
      .replace( /__\/ais-highlight__/g, '</span>' )
  } else {
    for ( var key in item ) {
      item[ key ] = replaceHighlightsRecursive( item[ key ])
    }
  }
  return item
}

export function startWidthPolyfill() {
  if ( !String.prototype.startsWith ) {
    Object.defineProperty( String.prototype, 'startsWith', {
      value: function( search, rawPos ) {
        var pos = rawPos > 0 ? rawPos|0 : 0
        return this.substring( pos, pos + search.length ) === search
      }
    });
  }
}
